import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock, AlertTriangle, CheckCircle, XCircle, ChevronLeft,
  ChevronRight, Lock, User, Mail, Users, FileText, Award, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PlFinalExamPage = () => {
  const navigate = useNavigate();

  const [examState, setExamState] = useState('proctorInfo');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [proctorName, setProctorName] = useState('');
  const [proctorEmail, setProctorEmail] = useState('');
  const [proctorRelationship, setProctorRelationship] = useState('');
  const [attestation, setAttestation] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(180 * 60);
  const [results, setResults] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    loadExam();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  useEffect(() => {
    if (examState === 'exam' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(true); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [examState]);

  const loadExam = async () => {
    try {
      const res = await axios.get(`${API}/pre-licensing/final-exam`);
      setQuestions(res.data.questions);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Complete all 7 chapters before taking the final exam');
        navigate('/pre-licensing/dashboard');
      } else {
        toast.error('Failed to load exam');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const handleProctorSubmit = () => {
    if (!proctorName.trim() || !proctorEmail.trim() || !proctorRelationship.trim()) {
      toast.error('Please fill in all proctor information'); return;
    }
    if (!attestation) { toast.error('Please confirm the attestation'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(proctorEmail)) {
      toast.error('Please enter a valid email'); return;
    }
    setExamState('instructions');
  };

  const startExam = () => { setExamStartTime(Date.now()); setExamState('exam'); };

  const handleAnswerSelect = (qId, letter) => {
    setAnswers(prev => ({ ...prev, [qId]: letter }));
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) setCurrentQuestionIndex(i => i + 1);
  };

  const handleSubmit = useCallback(async (timeExpired = false) => {
    if (!timeExpired) {
      const unanswered = questions.filter(q => !answers[q.id]).length;
      if (unanswered > 0 && !window.confirm(`You have ${unanswered} unanswered questions. Submit anyway?`)) return;
    }
    setSubmitting(true);
    const timeTaken = Math.floor((Date.now() - examStartTime) / 1000);
    try {
      const res = await axios.post(`${API}/pre-licensing/final-exam/submit`, {
        answers, time_taken_seconds: timeTaken,
        proctor_name: proctorName, proctor_email: proctorEmail, proctor_relationship: proctorRelationship,
      });
      setResults(res.data);
      setExamState('submitted');
      res.data.passed ? toast.success('Congratulations! You passed!') : toast.error('You did not pass. Review the material and try again.');
    } catch {
      toast.error('Failed to submit exam.');
    } finally {
      setSubmitting(false);
    }
  }, [answers, examStartTime, proctorName, proctorEmail, proctorRelationship, questions]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-primary">Loading Final Exam...</div>
    </div>
  );

  // ──── Proctor Info ────
  if (examState === 'proctorInfo') return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/pre-licensing/dashboard')} data-testid="pl-exam-back-btn">
            <ChevronLeft className="w-5 h-5 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-card border border-border rounded-sm p-8">
          <div className="text-center mb-8">
            <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              PRE-LICENSING FINAL EXAM
            </h1>
            <p className="text-muted-foreground">150 Questions &bull; 3 Hour Time Limit &bull; 75% Passing Score</p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-500 mb-1">Proctor Required</h3>
                <p className="text-sm text-muted-foreground">
                  This exam requires a proctor to verify your identity and ensure exam integrity.
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <Label htmlFor="plProctorName" className="flex items-center gap-2 mb-2"><User className="w-4 h-4" /> Proctor Full Name *</Label>
              <Input id="plProctorName" value={proctorName} onChange={e => setProctorName(e.target.value)} placeholder="Enter proctor's full legal name" data-testid="pl-proctor-name" />
            </div>
            <div>
              <Label htmlFor="plProctorEmail" className="flex items-center gap-2 mb-2"><Mail className="w-4 h-4" /> Proctor Email *</Label>
              <Input id="plProctorEmail" type="email" value={proctorEmail} onChange={e => setProctorEmail(e.target.value)} placeholder="Enter proctor's email address" data-testid="pl-proctor-email" />
            </div>
            <div>
              <Label htmlFor="plProctorRel" className="flex items-center gap-2 mb-2"><Users className="w-4 h-4" /> Relationship to Candidate *</Label>
              <Input id="plProctorRel" value={proctorRelationship} onChange={e => setProctorRelationship(e.target.value)} placeholder="e.g., Employer, Supervisor, Colleague" data-testid="pl-proctor-relationship" />
            </div>
            <div className="bg-secondary/50 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <Checkbox id="plAttestation" checked={attestation} onCheckedChange={setAttestation} data-testid="pl-attestation-checkbox" />
                <Label htmlFor="plAttestation" className="text-sm cursor-pointer">
                  I attest that the proctor named above will be physically present during my exam,
                  will verify my identity, and will ensure I complete this exam without unauthorized
                  assistance or materials.
                </Label>
              </div>
            </div>
            <Button className="w-full" onClick={handleProctorSubmit} disabled={!proctorName || !proctorEmail || !proctorRelationship || !attestation} data-testid="pl-continue-instructions">
              Continue to Exam Instructions
            </Button>
          </div>
        </div>
      </main>
    </div>
  );

  // ──── Instructions ────
  if (examState === 'instructions') return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => setExamState('proctorInfo')} data-testid="pl-back-proctor">
            <ChevronLeft className="w-5 h-5 mr-2" /> Back to Proctor Info
          </Button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-card border border-border rounded-sm p-8">
          <div className="text-center mb-8">
            <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>EXAM INSTRUCTIONS</h1>
          </div>
          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm">
              <Clock className="w-6 h-6 text-primary mt-0.5" />
              <div><h3 className="font-semibold mb-1">Time Limit</h3><p className="text-sm text-muted-foreground">You have <strong>3 hours</strong> to complete all 150 questions. The exam auto-submits when time expires.</p></div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm">
              <Lock className="w-6 h-6 text-amber-500 mt-0.5" />
              <div><h3 className="font-semibold mb-1">No Going Back</h3><p className="text-sm text-muted-foreground">Once you move to the next question, you <strong>cannot go back</strong>.</p></div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm">
              <AlertTriangle className="w-6 h-6 text-destructive mt-0.5" />
              <div><h3 className="font-semibold mb-1">Randomized</h3><p className="text-sm text-muted-foreground">Questions and answer choices are randomized. Each exam is unique.</p></div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-green-500/10 rounded-sm">
              <CheckCircle className="w-6 h-6 text-green-500 mt-0.5" />
              <div><h3 className="font-semibold mb-1">Passing Score</h3><p className="text-sm text-muted-foreground">You must score <strong>75% or higher</strong> (113 out of 150 correct) to pass.</p></div>
            </div>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-8">
            <p className="text-sm"><strong>Proctor:</strong> {proctorName} ({proctorEmail})</p>
          </div>
          <Button className="w-full bg-primary hover:bg-primary/90" size="lg" onClick={startExam} data-testid="pl-start-exam-btn">
            START EXAM
          </Button>
        </div>
      </main>
    </div>
  );

  // ──── Exam ────
  if (examState === 'exam') {
    const q = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isLast = currentQuestionIndex === questions.length - 1;
    const curAns = answers[q?.id];

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card fixed top-0 left-0 right-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <span className="font-bold" style={{ fontFamily: 'Barlow Condensed' }}>PRE-LICENSING FINAL EXAM</span>
            <div className="flex items-center gap-6">
              <div className="text-sm">Question <strong>{currentQuestionIndex + 1}</strong> of <strong>{questions.length}</strong></div>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-sm ${timeRemaining < 300 ? 'bg-destructive/20 text-destructive' : timeRemaining < 900 ? 'bg-amber-500/20 text-amber-500' : 'bg-secondary'}`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
              </div>
            </div>
          </div>
          <Progress value={progress} className="h-1" />
        </header>

        <main className="pt-24 pb-32 px-6">
          <div className="max-w-3xl mx-auto">
            {q && (
              <div className="bg-card border border-border rounded-sm p-8">
                <div className="mb-2 text-sm text-muted-foreground">
                  {q.question_type === 'true_false' ? 'True/False' : 'Multiple Choice'}
                </div>
                <h2 className="text-xl font-semibold mb-6 leading-relaxed">{q.question_text}</h2>
                <div className="space-y-3">
                  {q.options.map((opt, idx) => {
                    const letter = String.fromCharCode(65 + idx);
                    const sel = curAns === letter;
                    return (
                      <button key={idx} onClick={() => handleAnswerSelect(q.id, letter)}
                        className={`w-full text-left p-4 rounded-sm border transition-all ${sel ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-secondary/50'}`}
                        data-testid={`pl-answer-${letter}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${sel ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>{letter}</span>
                          <span className="flex-1 pt-1">{opt}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>

        <footer className="border-t border-border bg-card fixed bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{Object.keys(answers).length} of {questions.length} answered</div>
            {isLast ? (
              <Button onClick={() => handleSubmit(false)} disabled={!curAns || submitting} className="bg-green-600 hover:bg-green-700" data-testid="pl-submit-exam-btn">
                {submitting ? 'Submitting...' : 'Submit Exam'}
              </Button>
            ) : (
              <Button onClick={goToNextQuestion} disabled={!curAns} data-testid="pl-next-question-btn">
                Next Question <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </footer>
      </div>
    );
  }

  // ──── Results ────
  if (examState === 'submitted' && results) return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>EXAM RESULTS</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        {results.passed ? (
          <div className="space-y-8">
            {/* Score Card */}
            <div className="bg-card border-2 border-green-500/30 rounded-xl p-8 text-center" data-testid="pl-exam-results-passed">
              <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <Award className="w-14 h-14 text-green-500" />
              </div>
              <h2 className="text-4xl font-black text-green-500 mb-2 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>EXAMINATION PASSED</h2>
              <p className="text-lg text-muted-foreground mb-8">Colorado Bail Bonds Pre-Licensing Education</p>
              <div className="grid grid-cols-3 gap-4 mb-6 max-w-md mx-auto">
                <div className="bg-secondary rounded-lg p-4">
                  <div className="text-3xl font-black text-green-500" style={{ fontFamily: 'Barlow Condensed' }}>{results.score}</div>
                  <div className="text-xs text-muted-foreground mt-1">Correct</div>
                </div>
                <div className="bg-secondary rounded-lg p-4">
                  <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>{results.total}</div>
                  <div className="text-xs text-muted-foreground mt-1">Total</div>
                </div>
                <div className="bg-secondary rounded-lg p-4">
                  <div className="text-3xl font-black text-green-500" style={{ fontFamily: 'Barlow Condensed' }}>{results.percentage}%</div>
                  <div className="text-xs text-muted-foreground mt-1">Score</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Passing Score: 75% (113/150)</p>
            </div>

            {/* Author Letter */}
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="pl-author-letter">
              <div className="bg-amber-600/10 border-b border-amber-600/20 px-8 py-4">
                <p className="text-xs font-bold tracking-widest text-amber-600 uppercase">A Letter From the Author</p>
              </div>
              <div className="px-8 py-8 space-y-4 text-[0.95rem] leading-relaxed">
                <p>Congratulations.</p>
                <p>You have completed the Colorado Bail Bonds Pre-Licensing Education course and passed the final examination. That sentence is simple. What it represents is not.</p>
                <p>Over the past seven chapters, you have studied the legal framework that governs every bail bond transaction in Colorado. You have learned the statutes, the regulations, the court procedures, and the professional standards that define this profession. You have worked through the underwriting framework that separates a premium collector from a professional. You have studied forfeitures and recovery with the understanding that what happens after the bond breaks is as important as what happened before it was written. And you have confronted the two things that no statute can mandate and no examination can verify &mdash; common sense and ethical decision-making.</p>
                <p>The examination you just passed tested your knowledge of the material. The career ahead of you will test something deeper &mdash; your willingness to apply that knowledge under pressure, under financial stress, and in moments where the easier path and the correct path are not the same. The frameworks in this course were built for those moments.</p>
                <p>You are entering a profession that matters. The defendant standing in front of you is a human being whose freedom depends on your professional judgment. The indemnitor who signs is a person who trusted you. The court that accepts your bond trusted the system that licensed you. Every decision you make in this profession is made inside that web of trust.</p>
                <p>Exercise common sense. Make ethical decisions. Everything else is structure built on that foundation.</p>
                <p className="mt-6 font-semibold">Welcome to the profession.</p>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="font-bold">Colorado Bail Enforcement Agency</p>
                  <p className="text-sm text-muted-foreground">Pre-Licensing Education Program</p>
                </div>
              </div>
            </div>

            {/* Certificate Status */}
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6 text-center" data-testid="pl-certificate-status">
              <Shield className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <p className="font-semibold text-green-600 mb-1">Your certificate is pending admin review</p>
              <p className="text-sm text-muted-foreground">Your proctor ({proctorName}) may be contacted for verification. You will be notified when your certificate is approved.</p>
            </div>

            <div className="text-center">
              <Button onClick={() => navigate('/pre-licensing/dashboard')} size="lg" className="bg-amber-600 hover:bg-amber-700" data-testid="pl-return-dashboard-btn">
                Return to Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl p-8 text-center" data-testid="pl-exam-results-failed">
            <XCircle className="w-20 h-20 text-destructive mx-auto mb-4" />
            <h2 className="text-3xl font-black text-destructive mb-2" style={{ fontFamily: 'Barlow Condensed' }}>NOT PASSED</h2>
            <p className="text-xl mb-6">Review the course material and try again.</p>
            <div className="grid grid-cols-3 gap-4 mb-8 max-w-md mx-auto">
              <div className="bg-secondary rounded-lg p-4">
                <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>{results.score}</div>
                <div className="text-sm text-muted-foreground">Correct</div>
              </div>
              <div className="bg-secondary rounded-lg p-4">
                <div className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>{results.total}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <div className="bg-secondary rounded-lg p-4">
                <div className="text-3xl font-black text-destructive" style={{ fontFamily: 'Barlow Condensed' }}>{results.percentage}%</div>
                <div className="text-sm text-muted-foreground">Score</div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground mb-6">Passing Score: 75% (113/150)</div>
            <Button onClick={() => navigate('/pre-licensing/dashboard')} data-testid="pl-return-dashboard-btn">Return to Dashboard</Button>
          </div>
        )}
      </main>
    </div>
  );

  return null;
};

export default PlFinalExamPage;
